<template>
  <BaseModalLarge
    :heading="isEditing ? $t('services.price_update.edit_price_update_heading') : $t('products.update_prices')"
    :parentRoute="parentRouteName"
    @close="emit('close')"
    testId="create-price-update"
  >
    <Flex direction="column">
      <BaseAlert
        v-if="scheduledPriceUpdate && !isEditing"
        :text="`${$t('services.price_update.scheduled_price_update', { date: filters.dateTime(scheduledPriceUpdate.scheduledAt), amount: serviceAmount }, serviceAmount)} ${$t('services.price_update.cannot_create_extra_schedule')}`"
        color="warning"
        :primaryAction="$t('global.actions.cancel')"
        :secondaryAction="$t('global.actions.edit')"
        @primaryAction="cancelScheduledPriceUpdate"
        @secondaryAction="router.push({ name: 'admin-service-edit-price-update' })"
        v-test="'scheduled-price-update-warning'"
      />
      <Services v-model="tableData" />
    </Flex>
    <template #footer>
      <ModalFooter
        v-model="tableData"
        :isEditing="isEditing"
        @submitted="onSubmitted"
      />
    </template>
  </BaseModalLarge>
</template>

<script lang="ts">
export default {
  name: 'UpdatePrices',
};
</script>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import filters from '@/filters';
import { useServiceGroupsStore } from '@/stores/service-groups';
import { useServicesPriceScheduleStore } from '@/stores/services-price-schedule';
import ModalFooter from './ModalFooter.vue';
import Services from './Services.vue';
import type { TableData } from './types';

const props = defineProps<{
  parentRouteName?: string;
}>();

const emit = defineEmits(['close']);

const tableData = ref<TableData[]>([]);

const { flattenedServices } = storeToRefs(useServiceGroupsStore());

const { cancelScheduledPriceUpdate } = useServicesPriceScheduleStore();
const { scheduledPriceUpdate, serviceAmount, scheduleFetched } = storeToRefs(
  useServicesPriceScheduleStore(),
);

const route = useRoute();
const isEditing = computed(
  () => route.name === 'admin-service-edit-price-update',
);

const initTableData = () => {
  tableData.value = flattenedServices.value.map((service) => {
    const existingChange = isEditing.value
      ? scheduledPriceUpdate.value?.servicePriceChanges?.find(
          (change) => change.service.id === service.id,
        )
      : null;
    return {
      id: existingChange?.id || undefined,
      name: service.name,
      serviceId: service.id,
      originalPrice: existingChange?.originalPrice || service.price,
      newPrice: existingChange?.newPrice || service.price,
    };
  });
};

watch(
  flattenedServices,
  () => {
    initTableData();
  },
  {
    immediate: true,
  },
);

watch(scheduledPriceUpdate, () => {
  initTableData();
});

watch(isEditing, () => {
  initTableData();
});

const router = useRouter();

const onSubmitted = () => {
  if (props.parentRouteName) {
    router.push({ name: props.parentRouteName });
  } else {
    emit('close');
  }
};

watch(
  scheduleFetched,
  () => {
    if (
      isEditing.value &&
      scheduleFetched.value &&
      !scheduledPriceUpdate.value &&
      props.parentRouteName
    ) {
      router.push({ name: props.parentRouteName });
    }
  },
  {
    immediate: true,
  },
);
</script>
